import type { ColumnDef } from '@tanstack/react-table';
import { AlertCircle, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataStatePanel } from '@/components/ui/data-state-panel';
import { DataTable } from '@/components/ui/data-table';
import { Skeleton } from '@/components/ui/skeleton';

const LOADING_COLUMNS: ColumnDef<Record<string, never>, unknown>[] = ['Supplier', 'Host', 'Reported version', 'Status', 'Last checked'].map((header) => ({ id: header, header }));

const BackLink = ({ to }: { to: string }) => (
  <Button variant="ghost" size="sm" className="-ml-2 gap-2 text-muted-foreground" asChild>
    <Link to={to}>
      <ArrowLeft className="h-4 w-4" aria-hidden />
      Back to advisories
    </Link>
  </Button>
);

/**
 * ENT-6010: an advisory detail page while it loads — the final layout with skeletons, never a
 * full-page spinner (ui-ux-guidelines: Stable Shells, Loading States).
 */
export function AdvisoryPageLoading({ backTo }: { backTo: string }) {
  return (
    <div className="space-y-6" data-testid="advisory-loading" aria-busy="true">
      <div className="space-y-4">
        <BackLink to={backTo} />
        <PageHeader
          title={<span className="sr-only">Loading advisory</span>}
          titleRowClassName="gap-x-0"
          accessory={<Skeleton className="h-9 w-[28rem] max-w-full" />}
        />
        <div className="space-y-2">
          <Skeleton className="h-4 w-[48rem] max-w-full" />
          <Skeleton className="h-4 w-[36rem] max-w-full" />
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}
        </div>
      </div>
      <DataTable ariaLabel="Hosts in scope (loading)" columns={LOADING_COLUMNS} data={[]} loading loadingRows={5} />
    </div>
  );
}

/** ENT-6010: an advisory that could not load — header kept, error in context, with a retry. */
export function AdvisoryPageError({ backTo, message, onRetry, testId }: { backTo: string; message: string; onRetry: () => void; testId: string }) {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <BackLink to={backTo} />
        <PageHeader title="Advisory" />
      </div>
      <Alert variant="destructive" data-testid={testId}>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>This advisory could not be loaded</AlertTitle>
        <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
          <span>{message}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}

/** ENT-6010: no organisation the viewer can see is in scope for this advisory. */
export function AdvisoryNothingInScope() {
  return (
    <DataStatePanel
      icon={ShieldCheck}
      title="No affected hosts"
      description="No hosts in this context report the affected product."
      data-testid="advisory-empty"
    />
  );
}

/** ENT-6091: a missing open resource is quiet and never triggers a listing RPC. */
export function AdvisoryContextMissing({ vendor = false }: { vendor?: boolean }) {
  return <DataStatePanel title="Advisories" description={vendor ? 'Choose a supplier profile to view its advisories.' : 'Choose an organisation to view its advisories.'} data-testid="advisory-context-missing" />;
}

export function AdvisoryContextDisabled({ companyName }: { companyName: string }) {
  return <DataStatePanel title="Advisories" description={`Runtime advisories aren't switched on for ${companyName} yet.`} data-testid="runtime-advisories-disabled" />;
}
